// Walking Tours Service - Frontend API client
// Manages walking tours creation, sharing, and discovery

import { API_BASE } from '../config/api';
import AuthService from './authService';
import { formatDistance } from '../utils/distanceFormat';

class WalkingToursService {
  static getAuthHeaders() {
    return {
      ...AuthService.getAuthHeaders(),
      'Content-Type': 'application/json'
    };
  }

  static async getCurrentUserId() {
    const user = await AuthService.getCurrentUser();
    return user?.id || null;
  }

  // ==================== TOUR CRUD ====================

  /**
   * Create a new walking tour
   */
  static async createTour(tourData) {
    try {
      const userId = await this.getCurrentUserId();
      if (!userId) {
        return { success: false, error: 'Not authenticated' };
      }

      const response = await fetch(`${API_BASE}/api/walking-tours`, {
        method: 'POST',
        headers: await this.getAuthHeaders(),
        body: JSON.stringify({ userId, ...tourData })
      });

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.error || 'Failed to create tour' };
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating tour:', error);
      return { success: false, error: 'Failed to create tour' };
    }
  }

  /**
   * Create a walking tour with stops in a single transaction.
   * Accepts place metadata for stops (no pre-existing activity_id needed).
   */
  static async createTourWithStops(tourData, stops) {
    try {
      const userId = await this.getCurrentUserId();
      if (!userId) {
        return { success: false, error: 'Not authenticated' };
      }

      const response = await fetch(`${API_BASE}/api/walking-tours/create-with-stops`, {
        method: 'POST',
        headers: await this.getAuthHeaders(),
        body: JSON.stringify({ tourData, stops })
      });

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.error || 'Failed to create tour with stops' };
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating tour with stops:', error);
      return { success: false, error: 'Failed to create tour with stops' };
    }
  }

  /**
   * Get a tour by ID
   */
  static async getTour(tourId) {
    try {
      const userId = await this.getCurrentUserId();
      const response = await fetch(
        `${API_BASE}/api/walking-tours/${tourId}?userId=${userId || ''}`,
        { headers: await this.getAuthHeaders() }
      );

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.error || 'Failed to get tour' };
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting tour:', error);
      return { success: false, error: 'Failed to get tour' };
    }
  }

  /**
   * Get all walking tours for a trip
   */
  static async getToursByTripId(tripId) {
    try {
      const response = await fetch(
        `${API_BASE}/api/walking-tours/trip/${tripId}`,
        { headers: await this.getAuthHeaders() }
      );

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.error || 'Failed to get trip tours' };
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting trip walking tours:', error);
      return { success: false, error: 'Failed to get trip walking tours' };
    }
  }

  /**
   * Get a placeId-keyed map of all walking tour stops for a trip.
   * Returns { success, stopMap: { placeId: { tourName, tourId } } }
   */
  static async getTripTourStopMap(tripId) {
    try {
      const response = await fetch(
        `${API_BASE}/api/walking-tours/trip/${tripId}/stop-map`,
        { headers: await this.getAuthHeaders() }
      );

      if (!response.ok) {
        return { success: false, stopMap: {} };
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting trip tour stop map:', error);
      return { success: false, stopMap: {} };
    }
  }

  /**
   * Get a tour by share code
   */
  static async getTourByShareCode(shareCode) {
    try {
      const userId = await this.getCurrentUserId();
      const response = await fetch(
        `${API_BASE}/api/walking-tours/share/${shareCode}?userId=${userId || ''}`,
        { headers: await this.getAuthHeaders() }
      );

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.error || 'Failed to get tour' };
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting tour by share code:', error);
      return { success: false, error: 'Failed to get tour' };
    }
  }

  /**
   * Get all tours for the current user
   */
  static async getUserTours() {
    try {
      const userId = await this.getCurrentUserId();
      if (!userId) {
        return { success: false, error: 'Not authenticated' };
      }

      const response = await fetch(
        `${API_BASE}/api/walking-tours/user/${userId}`,
        { headers: await this.getAuthHeaders() }
      );

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.error || 'Failed to get tours' };
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting user tours:', error);
      return { success: false, error: 'Failed to get tours' };
    }
  }

  /**
   * Get tour preview for social feed
   */
  static async getTourPreview(tourId) {
    try {
      const userId = await this.getCurrentUserId();
      const response = await fetch(
        `${API_BASE}/api/walking-tours/${tourId}/preview?userId=${userId || ''}`,
        { headers: await this.getAuthHeaders() }
      );

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.error || 'Failed to get preview' };
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting tour preview:', error);
      return { success: false, error: 'Failed to get preview' };
    }
  }

  /**
   * Update a tour
   */
  static async updateTour(tourId, updates) {
    try {
      const userId = await this.getCurrentUserId();
      if (!userId) {
        return { success: false, error: 'Not authenticated' };
      }

      const response = await fetch(`${API_BASE}/api/walking-tours/${tourId}`, {
        method: 'PUT',
        headers: await this.getAuthHeaders(),
        body: JSON.stringify({ userId, ...updates })
      });

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.error || 'Failed to update tour' };
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating tour:', error);
      return { success: false, error: 'Failed to update tour' };
    }
  }

  /**
   * Delete a tour
   */
  static async deleteTour(tourId) {
    try {
      const userId = await this.getCurrentUserId();
      if (!userId) {
        return { success: false, error: 'Not authenticated' };
      }

      const response = await fetch(`${API_BASE}/api/walking-tours/${tourId}`, {
        method: 'DELETE',
        headers: await this.getAuthHeaders(),
        body: JSON.stringify({ userId })
      });

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.error || 'Failed to delete tour' };
      }

      return await response.json();
    } catch (error) {
      console.error('Error deleting tour:', error);
      return { success: false, error: 'Failed to delete tour' };
    }
  }

  // ==================== STOP MANAGEMENT ====================

  /**
   * Get stops for a tour
   */
  static async getTourStops(tourId) {
    try {
      const response = await fetch(
        `${API_BASE}/api/walking-tours/${tourId}/stops`,
        { headers: await this.getAuthHeaders() }
      );

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.error || 'Failed to get stops' };
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting tour stops:', error);
      return { success: false, error: 'Failed to get stops' };
    }
  }

  /**
   * Add a stop from a place (creates activity record automatically)
   */
  static async addStopFromPlace(tourId, stopData) {
    try {
      const userId = await this.getCurrentUserId();
      if (!userId) {
        return { success: false, error: 'Not authenticated' };
      }

      const response = await fetch(`${API_BASE}/api/walking-tours/${tourId}/stops/from-place`, {
        method: 'POST',
        headers: await this.getAuthHeaders(),
        body: JSON.stringify({ userId, ...stopData })
      });

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.error || 'Failed to add stop' };
      }

      return await response.json();
    } catch (error) {
      console.error('Error adding stop from place:', error);
      return { success: false, error: 'Failed to add stop' };
    }
  }

  /**
   * Add a stop to a tour
   */
  static async addStop(tourId, stopData) {
    try {
      const userId = await this.getCurrentUserId();
      if (!userId) {
        return { success: false, error: 'Not authenticated' };
      }

      const response = await fetch(`${API_BASE}/api/walking-tours/${tourId}/stops`, {
        method: 'POST',
        headers: await this.getAuthHeaders(),
        body: JSON.stringify({ userId, ...stopData })
      });

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.error || 'Failed to add stop' };
      }

      return await response.json();
    } catch (error) {
      console.error('Error adding stop:', error);
      return { success: false, error: 'Failed to add stop' };
    }
  }

  /**
   * Update a stop
   */
  static async updateStop(tourId, stopActivityId, updates) {
    try {
      const userId = await this.getCurrentUserId();
      if (!userId) {
        return { success: false, error: 'Not authenticated' };
      }

      const response = await fetch(
        `${API_BASE}/api/walking-tours/${tourId}/stops/${stopActivityId}`,
        {
          method: 'PUT',
          headers: await this.getAuthHeaders(),
          body: JSON.stringify({ userId, ...updates })
        }
      );

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.error || 'Failed to update stop' };
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating stop:', error);
      return { success: false, error: 'Failed to update stop' };
    }
  }

  /**
   * Remove a stop from a tour
   */
  static async removeStop(tourId, stopActivityId) {
    try {
      const userId = await this.getCurrentUserId();
      if (!userId) {
        return { success: false, error: 'Not authenticated' };
      }

      const response = await fetch(
        `${API_BASE}/api/walking-tours/${tourId}/stops/${stopActivityId}`,
        {
          method: 'DELETE',
          headers: await this.getAuthHeaders(),
          body: JSON.stringify({ userId })
        }
      );

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.error || 'Failed to remove stop' };
      }

      return await response.json();
    } catch (error) {
      console.error('Error removing stop:', error);
      return { success: false, error: 'Failed to remove stop' };
    }
  }

  /**
   * Reorder stops in a tour
   */
  static async reorderStops(tourId, newOrder) {
    try {
      const userId = await this.getCurrentUserId();
      if (!userId) {
        return { success: false, error: 'Not authenticated' };
      }

      const response = await fetch(`${API_BASE}/api/walking-tours/${tourId}/reorder`, {
        method: 'PUT',
        headers: await this.getAuthHeaders(),
        body: JSON.stringify({ userId, newOrder })
      });

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.error || 'Failed to reorder stops' };
      }

      return await response.json();
    } catch (error) {
      console.error('Error reordering stops:', error);
      return { success: false, error: 'Failed to reorder stops' };
    }
  }

  /**
   * Reconcile a tour's metadata and stop list in one call — the single save
   * path shared by the standalone WalkingTourBuilder and the in-planner
   * EditWalkingTourPanel. Adds are persisted live (via addStopFromPlace) before
   * this runs, so the host passes the already-current `stops`; this method
   * handles metadata, removals, per-stop duration edits, and final ordering.
   *
   * @param {string} tourId
   * @param {object} [metadata]      Fields for updateTour (skipped if null/empty)
   * @param {Array}  originalStops   Stops present when editing began (objects, for
   *                                 removal + duration diffing)
   * @param {Array}  stops           Current ordered stops ({ stopActivityId, durationMinutes })
   * @returns {{success: boolean, error?: string}}
   */
  static async saveTourEdits(tourId, metadata, originalStops, stops) {
    try {
      if (metadata && Object.keys(metadata).length > 0) {
        const metaResult = await this.updateTour(tourId, metadata);
        if (!metaResult.success) {
          return { success: false, error: metaResult.error || 'Failed to update tour details' };
        }
      }

      const originalById = new Map(
        (originalStops || [])
          .filter(s => s.stopActivityId)
          .map(s => [s.stopActivityId, s])
      );
      const currentIds = stops.map(s => s.stopActivityId).filter(Boolean);
      const currentIdSet = new Set(currentIds);

      // Removals: stops that were present originally but no longer are
      for (const id of originalById.keys()) {
        if (currentIdSet.has(id)) continue;
        const result = await this.removeStop(tourId, id);
        if (!result.success) {
          return { success: false, error: result.error || 'Failed to remove a stop' };
        }
      }

      // Per-stop duration edits (only for stops that still exist and changed)
      for (const stop of stops) {
        if (!stop.stopActivityId) continue;
        const orig = originalById.get(stop.stopActivityId);
        // A stop made quick (no length) is a change too: it is sent as null.
        if (orig && (orig.durationMinutes || null) !== (stop.durationMinutes || null)) {
          const result = await this.updateStop(tourId, stop.stopActivityId, {
            durationMinutes: stop.durationMinutes || null
          });
          if (!result.success) {
            return { success: false, error: result.error || 'Failed to update a stop' };
          }
        }
      }

      if (currentIds.length > 1) {
        const result = await this.reorderStops(tourId, currentIds);
        if (!result.success) {
          return { success: false, error: result.error || 'Failed to reorder stops' };
        }
      }

      return { success: true };
    } catch (error) {
      console.error('Error saving tour edits:', error);
      return { success: false, error: 'Failed to save tour' };
    }
  }

  // ==================== SHARING & COPYING ====================

  /**
   * Copy a tour to user's own tours
   */
  static async copyTour(tourId) {
    try {
      const userId = await this.getCurrentUserId();
      if (!userId) {
        return { success: false, error: 'Not authenticated' };
      }

      const response = await fetch(`${API_BASE}/api/walking-tours/${tourId}/copy`, {
        method: 'POST',
        headers: await this.getAuthHeaders(),
        body: JSON.stringify({ userId })
      });

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.error || 'Failed to copy tour' };
      }

      return await response.json();
    } catch (error) {
      console.error('Error copying tour:', error);
      return { success: false, error: 'Failed to copy tour' };
    }
  }

  /**
   * Save a tour reference (bookmark)
   */
  static async saveTour(tourId, notes = null) {
    try {
      const userId = await this.getCurrentUserId();
      if (!userId) {
        return { success: false, error: 'Not authenticated' };
      }

      const response = await fetch(`${API_BASE}/api/walking-tours/${tourId}/save`, {
        method: 'POST',
        headers: await this.getAuthHeaders(),
        body: JSON.stringify({ userId, notes })
      });

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.error || 'Failed to save tour' };
      }

      return await response.json();
    } catch (error) {
      console.error('Error saving tour:', error);
      return { success: false, error: 'Failed to save tour' };
    }
  }

  /**
   * Unsave a tour reference
   */
  static async unsaveTour(tourId) {
    try {
      const userId = await this.getCurrentUserId();
      if (!userId) {
        return { success: false, error: 'Not authenticated' };
      }

      const response = await fetch(
        `${API_BASE}/api/walking-tours/${tourId}/save?userId=${userId}`,
        {
          method: 'DELETE',
          headers: await this.getAuthHeaders()
        }
      );

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.error || 'Failed to unsave tour' };
      }

      return await response.json();
    } catch (error) {
      console.error('Error unsaving tour:', error);
      return { success: false, error: 'Failed to unsave tour' };
    }
  }

  /**
   * Get user's saved tours
   */
  static async getSavedTours() {
    try {
      const response = await fetch(
        `${API_BASE}/api/walking-tours/saved`,
        { headers: await this.getAuthHeaders() }
      );

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.error || 'Failed to get saved tours' };
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting saved tours:', error);
      return { success: false, error: 'Failed to get saved tours' };
    }
  }

  /**
   * Put a walking tour on a trip's board (it can be on any number of trips)
   */
  static async associateWithTrip(tourId, tripId) {
    try {
      const response = await fetch(`${API_BASE}/api/walking-tours/${tourId}/trip`, {
        method: 'PATCH',
        headers: await this.getAuthHeaders(),
        body: JSON.stringify({ tripId })
      });

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.error || 'Failed to update tour trip association' };
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating tour trip association:', error);
      return { success: false, error: 'Failed to update tour trip association' };
    }
  }

  /**
   * Take a walking tour off one trip's board (trip editors only)
   */
  static async removeFromTrip(tourId, tripId) {
    try {
      const response = await fetch(`${API_BASE}/api/walking-tours/${tourId}/trip/${tripId}`, {
        method: 'DELETE',
        headers: await this.getAuthHeaders()
      });

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.error || 'Failed to remove tour from trip' };
      }

      return await response.json();
    } catch (error) {
      console.error('Error removing tour from trip:', error);
      return { success: false, error: 'Failed to remove tour from trip' };
    }
  }

  // ==================== SOCIAL INTERACTIONS ====================

  /**
   * Toggle like on a tour
   */
  static async toggleLike(tourId) {
    try {
      const userId = await this.getCurrentUserId();
      if (!userId) {
        return { success: false, error: 'Not authenticated' };
      }

      const response = await fetch(`${API_BASE}/api/walking-tours/${tourId}/like`, {
        method: 'POST',
        headers: await this.getAuthHeaders(),
        body: JSON.stringify({ userId })
      });

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.error || 'Failed to toggle like' };
      }

      return await response.json();
    } catch (error) {
      console.error('Error toggling like:', error);
      return { success: false, error: 'Failed to toggle like' };
    }
  }

  // ==================== HELPERS ====================

  /**
   * Format distance for display
   */
  static formatDistance(meters, unit = 'metric') {
    if (!meters) return null;
    return formatDistance(meters, unit);
  }

  /**
   * Format walking time for display
   */
  static formatWalkingTime(minutes) {
    if (!minutes) return null;
    if (minutes < 60) {
      return `${minutes} min`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
  }

  /**
   * Get difficulty badge color using semantic tokens from style guide
   */
  static getDifficultyColor(difficulty) {
    switch (difficulty) {
      case 'easy': return { bg: 'bg-success-light', text: 'text-success-dark' };
      case 'moderate': return { bg: 'bg-warning-light', text: 'text-warning-dark' };
      case 'challenging': return { bg: 'bg-error-light', text: 'text-error-dark' };
      default: return { bg: 'bg-surface-secondary', text: 'text-content-secondary' };
    }
  }

  /**
   * Get accessibility icons
   */
  static getAccessibilityIcons(tour) {
    const icons = [];
    if (tour.isStrollerFriendly) icons.push({ icon: '👶', label: 'Stroller friendly' });
    if (tour.isWheelchairAccessible) icons.push({ icon: '♿', label: 'Wheelchair accessible' });
    return icons;
  }
}

export default WalkingToursService;
